import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { currentProWeekIndex } from '@/pages/medical/screens/home/utils';
import { BiometricTab } from '@/pages/medical/screens/patients/BiometricTab';
import { InterventionTab } from '@/pages/medical/screens/patients/InterventionTab';
import { PatientDetailHeader } from '@/pages/medical/screens/patients/PatientDetailHeader';
import { ProCalendar } from '@/pages/medical/screens/patients/ProCalendar';
import { ProToolbar } from '@/pages/medical/screens/patients/ProToolbar';
import { usePatientDetail } from '@/shared/hooks/use-patient-actions';

type TabKey = 'pro' | 'biometric' | 'intervention';
const TABS: TabKey[] = ['pro', 'biometric', 'intervention'];

interface Props {
  patientUid: string;
}

/**
 * The one patient an EMR session may view (SHCT-994, plan D20). The patient list screen cannot be reused: on entry it
 * calls the list, the counts and (through the GNB) notifications — all outside the EMR allow-list — and its detail
 * query waits for the list to succeed. This page reads only the detail and the tabs' own per-patient GETs
 * (api-contracts/emr-bridge.md §B.1.1). Writes are closed by the auth store (no write role in an EMR session) and the
 * CSV buttons are not rendered; the intervention panel and the schedule modal are not mounted at all.
 */
export function EmrPatientPage({ patientUid }: Props) {
  const { t } = useTranslation(['medical']);
  const [activeTab, setActiveTab] = useState<TabKey>('pro');
  const { data: patient, isError } = usePatientDetail(patientUid);
  // The header asks for the number in the clear; for an EMR session the server answers masked (B.1.1).
  const { data: unmasked } = usePatientDetail(patientUid, true);

  if (isError) {
    return (
      <div className='pm-right__empty'>
        <p>{t('medical:patients.errors.loadPatient')}</p>
      </div>
    );
  }
  if (!patient) return null;

  return (
    <div className='pm-layout pm-layout--v31 pm-layout--emr'>
      <section className='pm-right'>
        <PatientDetailHeader patient={patient} phone={unmasked?.phone} onScheduleAdd={() => {}} />
        <nav className='ph-tabs'>
          {TABS.map((key) => (
            <button
              key={key}
              type='button'
              className={`ph-tabs__item${activeTab === key ? ' ph-tabs__item--active' : ''}`}
              onClick={() => setActiveTab(key)}
            >
              {t(`medical:patients.tabs.${key}`)}
            </button>
          ))}
        </nav>
        <div key={activeTab} className='ph-content flex-1 overflow-auto p-6'>
          {activeTab === 'pro' && (
            <>
              <ProToolbar referenceDate={new Date().toISOString().slice(0, 10)} />
              <ProCalendar
                patientUid={patient.uid}
                currentWeek={currentProWeekIndex(patient.dischargeDate)}
                startDate={patient.dischargeDate}
              />
            </>
          )}
          {activeTab === 'biometric' && <BiometricTab patient={patient} />}
          {activeTab === 'intervention' && <InterventionTab patient={patient} />}
        </div>
      </section>
    </div>
  );
}
